import React from "react";
import { createRoot } from "react-dom/client";
import configureStore from "./Store/configureStore";
import { Provider } from "react-redux";
import { OidcProvider } from "redux-oidc";
import {
  ApolloProvider,
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import {
  setCulture,
  setCurrency,
  setPosId,
  setSaleId,
} from "./Actions/cookiesActions";
import { CONFIG_LOADED } from "./Actions/actionTypes";
import initialState from "./Reducers/initialState";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "./Styles/index.css";
import "./Styles/App.css";
import { getUserManager } from "./Api/auth";
import ContextProvider from "./context";
import Routes from "./routes";
import { loadLanguage } from "./Components/Common/language";
import { MantineProvider } from "@mantine/core";
import { emotionTransform, MantineEmotionProvider } from "@mantine/emotion";
import { Notifications } from "@mantine/notifications";
import Cookies from "universal-cookie";

const getJson = (f) => {
  return fetch(f).then((x) => x.json());
};

const merge = (o, rest) => {
  var next = rest.pop();
  if (!next) return o;

  const merged = {
    ...o,
    ...next,
  };

  return merge(merged, rest);
};

const cookies = new Cookies(null, { path: "/" });
const configUri = process.env.PUBLIC_URL + "/config.json";

fetch(configUri)
  .then((response) => response.json())
  .then((config) => {
    const localUri = `${config.baseUrl}/ee-static/checkout/pos-${initialState.posId}/config.json`;
    const files = [configUri, localUri].map((f) => getJson(f));

    Promise.all(files)
      .then((response) => merge({}, response))
      .then((config) => {
        //Add local stylesheet
        let link = window.document.createElement("link");
        link.href = `${config.baseUrl}/ee-static/checkout/pos-${initialState.posId}/style.css`;
        link.rel = "stylesheet";
        window.document.getElementsByTagName("head")[0].appendChild(link);

        document.title = `${config.shopName} - Checkout`;

        // if (config.useTagManager) {
        //   let tgm = window.document.createElement("script");
        //   tgm.setAttribute("type", "text/javascript");
        //   tgm.setAttribute(
        //     "src",
        //     `${process.env.PUBLIC_URL}/ee-static/checkout/pos-${initialState.posId}/Scripts/tgm.js`
        //   );
        //   window.document.getElementsByTagName("head")[0].appendChild(tgm);
        //   console.log("tagManager used");
        // }

        //create userManager
        const userManager = getUserManager(config);

        //New Apollo don't need redux
        const store = configureStore(initialState, userManager);

        const culture = initialState.culture || "sv-SE";

        //Create Apollo client and auth
        const httpLink = new HttpLink({ uri: `${config.apiUri}/graphql` });
        const authLink = new ApolloLink((operation, forward) => {
          const { oidc } = store.getState();

          const token = oidc.user ? oidc.user.access_token : null;
          if (token) {
            operation.setContext({
              headers: {
                authorization: token ? `Bearer ${token}` : null,
              },
            });
          }

          return forward(operation);
        });

        const container = window.document.getElementById("root");
        const root = createRoot(container);

        const client = new ApolloClient({
          link: authLink.concat(httpLink),
          cache: new InMemoryCache(),
        });
        const cookieLangShop = cookies.get("shop-culture");
        store.dispatch({ type: CONFIG_LOADED, payload: config });
        if (cookieLangShop) {
          store.dispatch(setCulture(cookieLangShop, config));
        } else {
          store.dispatch(setCulture(culture, config));
        }
        store.dispatch(setCurrency(initialState.currency || "SEK"));
        store.dispatch(setSaleId(initialState.saleId || ""));
        store.dispatch(setPosId(initialState.posId || 1));

        loadLanguage(culture, initialState.posId, store.dispatch, config);

        const optIn = {
          enabled: config.optIn !== undefined && config.optIn.enabled,
          isSoft: config.optIn !== undefined && config.optIn.isSoft,
        };

        root.render(
          <ApolloProvider client={client}>
            <Provider store={store}>
              <OidcProvider store={store} userManager={userManager}>
                <ContextProvider optIn={optIn} userManager={userManager}>
                  <MantineProvider
                    withNormalizeCSS
                    withGlobalStyles
                    stylesTransform={emotionTransform}
                  >
                    <MantineEmotionProvider>
                      <Routes userManager={userManager} />
                      <Notifications position="bottom-center" />
                    </MantineEmotionProvider>
                  </MantineProvider>
                </ContextProvider>
              </OidcProvider>
            </Provider>
          </ApolloProvider>
        );
      });
  });
